import {
  Button,
  Card,
  CardBody,
  CardFooter,
  Chip,
  Divider,
  Spacer,
} from '@nextui-org/react';
import { Carousel } from 'antd';
import { Colors } from '../../../config/theme';
import {
  AmazonScrappedListingType,
  SearchEngineQueryShortResult,
} from '../../../data/types/listing_scrapers';
import { UseTemplateConfirmationPopup } from '../modals/use_template_summary_modal';
import { FaAngleLeft, FaAngleRight } from 'react-icons/fa6';
import { ReactTyped } from 'react-typed';
import { sanitizeHTML } from '../../../utils/functions/formatting_functions';
import {
  SolrBuyerItemsResponse,
  SolrTemplateResponse,
} from '../../../data/types/listings';
import { SanitizedHTMLStyle } from '../../../styles/style';
import { ImageType } from '../../../data/types/files';

export const words = [
  'iPhone 15 Pro',
  'BLEU DE CHANEL Perfume',
  'Nike Air Max 2022',
  'MacBook Pro 2023',
  'Angels & Demons by Dan Brown',
];

export const generatingPorudctWords = [
  '🧬 Crafting the perfect product DNA...',
  '🛠️ Building your product unique blueprint...',
  '📦 Assembling all the details for you...',
  '🔍 Gathering attributes with precision...',
  '🖼️ Curating high-quality images...',
  '✍️ Writing detailed descriptions...',
  '📏 Measuring dimensions accurately...',
  '🎁 Unboxing the contents for you...',
  '🔄 Synchronizing all the data points...',
  '⚡ Enhancing your product information...',
];

export const infoStyle = {
  color: 'lightgray',
  marginRight: '1rem',
};

export const handleKeyPress = (e: KeyboardEvent) => {
  if (e.key === 'Enter') {
    document.getElementById('search-button')?.click();
  }
};

export const InfoRow = ({
  title,
  value,
  rtl = false,
  html = false,
}: {
  title: string;
  value?: string;
  rtl?: boolean;
  html?: boolean;
}) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
      padding: '0.5rem',
    }}
  >
    <div
      style={{
        flex: 1,
      }}
    >
      <p style={infoStyle}>
        <b>{title}</b>
      </p>
    </div>
    <div
      style={{
        flex: 4,
      }}
    >
      <p
        style={{
          color: value ? '' : 'red',
          textAlign: rtl ? 'right' : 'left',
          direction: rtl ? 'rtl' : 'ltr',
        }}
      >
        {html ? (
          <div
            dangerouslySetInnerHTML={sanitizeHTML(value ?? 'No Data Available')}
          />
        ) : (
          (value ?? 'No Data Available')
        )}
      </p>
    </div>
  </div>
);

export const AttributesInfo = ({
  official,
  attributes,
}: {
  official: boolean;
  attributes: { name_en: string; value: string; type: string }[];
}) => {
  return (
    <>
      <div
        style={{
          padding: '0.5rem',
        }}
      >
        <p style={infoStyle}>
          <b>{official ? 'Official Attributes' : 'Custom Attributes'}</b>
        </p>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'start',
          width: '100%',
          padding: '0.5rem',
        }}
      >
        <div
          style={{
            flex: 4,
          }}
        >
          {attributes.map((attribute, index) => (
            <Chip
              key={index}
              color={official ? 'primary' : 'warning'}
              variant="flat"
              style={{ margin: '0.5rem' }}
            >
              {attribute.name_en} ({attribute.type}): {attribute.value}
            </Chip>
          ))}
        </div>
      </div>
    </>
  );
};

export const AIListingCardResults = ({
  searchResults,
  results,
  loadDetails,
  s3Repository,
  handleClick,
  setCurrentImages,
}: {
  searchResults: (SearchEngineQueryShortResult & { country: string })[];
  results: {
    [key: string]: {
      loading: boolean;
      data: AmazonScrappedListingType | null;
    };
  };
  loadDetails: (asin: string, country: string) => void;
  s3Repository: any;
  handleClick: (index: number) => void;
  setCurrentImages: (images: string[]) => void;
}) => {
  return (
    <div
      style={{
        margin: '1rem',
        padding: '1rem',
        width: '100%',
        height: '100%',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        alignItems: 'start',
        gap: '1rem',
      }}
    >
      {searchResults.slice(0, 3).map((item, index) => (
        <Card shadow="sm" key={index}>
          {!results[item.asin]?.data ? (
            <CardBody className="p-0">
              <Carousel
                rootClassName="outline-none"
                style={{
                  outline: 'none',
                  border: '1px solid transparent',
                }}
                arrows
                infinite={true}
                effect="fade"
                prevArrow={<FaAngleLeft color={Colors.blueMain} size={30} />}
                nextArrow={<FaAngleRight color={Colors.blueMain} size={30} />}
              >
                <div key={index}>
                  <img
                    className="rounded-xl"
                    src={item.photo}
                    onClick={() => {
                      setCurrentImages([item.photo]);
                      handleClick(index);
                    }}
                    width={'100%'}
                    style={{
                      outline: 'none',
                      objectFit: 'scale-down',
                      height: '200px',
                      borderRadius: '1rem 1rem 0 0',
                      border: '1px solid transparent',
                    }}
                  />
                </div>
              </Carousel>
              <div
                style={{
                  padding: '1rem',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                }}
              >
                <InfoRow title="Name" value={item.title} />
              </div>
            </CardBody>
          ) : (
            <CardBody className="p-0">
              <Carousel
                rootClassName="outline-none"
                style={{
                  outline: 'none',
                  border: '1px solid transparent',
                }}
                arrows
                infinite={true}
                effect="fade"
                prevArrow={<FaAngleLeft color={Colors.blueMain} size={30} />}
                nextArrow={<FaAngleRight color={Colors.blueMain} size={30} />}
              >
                {results[item.asin]?.data?.images.map((image, index) => (
                  <div key={index}>
                    <img
                      className="rounded-xl"
                      src={image}
                      onClick={() => {
                        setCurrentImages(
                          results[item.asin]?.data?.images || [],
                        );
                        handleClick(index);
                      }}
                      width={'100%'}
                      style={{
                        outline: 'none',
                        objectFit: 'scale-down',
                        height: '200px',
                        borderRadius: '1rem 1rem 0 0',
                        border: '1px solid transparent',
                      }}
                    />
                  </div>
                ))}
              </Carousel>
              <div
                style={{
                  padding: '1rem',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  alignItems: 'flex-start',
                }}
              >
                <InfoRow
                  title="Name En"
                  value={results[item.asin]?.data?.titles?.title_en}
                />
                <InfoRow
                  title="Name Ar"
                  value={results[item.asin]?.data?.titles?.title_ar}
                  rtl
                />
                <InfoRow
                  title="Category"
                  value={results[item.asin]?.data?.category.name}
                  rtl
                />
                <InfoRow
                  title="Description En"
                  value={results[item.asin]?.data?.titles?.description_en}
                  html
                />
                <InfoRow
                  title="Description Ar"
                  value={results[item.asin]?.data?.titles?.description_ar}
                  html
                  rtl
                />
                <InfoRow
                  title="Whats in the box"
                  value={
                    results[item.asin]?.data?.dimensions?.box_contents ||
                    'No Data'
                  }
                />
                <InfoRow
                  title="Shipping"
                  value={
                    (results[item.asin]?.data?.dimensions
                      ? results[item.asin]?.data?.dimensions?.width_cm
                          ?.toFixed(2)
                          .toString() +
                        'x' +
                        results[item.asin]?.data?.dimensions?.height_cm
                          ?.toFixed(2)
                          .toString() +
                        'x' +
                        results[item.asin]?.data?.dimensions?.length_cm
                          ?.toFixed(2)
                          .toString() +
                        'x'
                      : 'No Dimensions, ') +
                    (results[item.asin]?.data?.dimensions.weight_g
                      ? results[item.asin]?.data?.dimensions.weight_g
                          ?.toFixed(2)
                          .toString() + 'g'
                      : 'No Weight')
                  }
                />
                <AttributesInfo
                  official
                  attributes={
                    results[item.asin]?.data?.attributes.silal_attributes?.map(
                      (attribute) => ({
                        name_en: attribute.name_en,
                        value: attribute.value
                          ? attribute.value.toString()
                          : '',
                        type: attribute.type_name,
                      }),
                    ) || []
                  }
                />
                <AttributesInfo
                  official={false}
                  attributes={
                    results[
                      item.asin
                    ]?.data?.attributes?.unofficial_attributes?.map(
                      (attribute) => ({
                        name_en: attribute.name_en,
                        value: attribute.value
                          ? attribute.value.toString()
                          : '',
                        type: attribute.type_name,
                      }),
                    ) || []
                  }
                />
              </div>
            </CardBody>
          )}
          <Divider />
          <CardFooter className="flex justify-center">
            {Object.keys(results).includes(item.asin) &&
            results[item.asin]?.loading ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                }}
              >
                <l-helix size="150" speed="2.5" color={Colors.greenMain} />
                <Spacer y={8} />
                <ReactTyped
                  style={{
                    color: 'lightgray',
                    fontSize: '1.5rem',
                    fontWeight: 600,
                    textAlign: 'center',
                  }}
                  strings={generatingPorudctWords}
                  typeSpeed={50}
                  loop
                  backSpeed={20}
                  cursorChar="|"
                  showCursor={true}
                />
              </div>
            ) : !results[item.asin]?.data ? (
              <Button
                className="bg-gradient-to-tr from-blue-500 to-green-500 text-white shadow-lg"
                onClick={() => loadDetails(item.asin, item.country)}
              >
                Load Details
              </Button>
            ) : (
              <UseTemplateConfirmationPopup
                s3Repository={s3Repository}
                amazonTemplate={results[item.asin]?.data!}
              />
            )}
          </CardFooter>
        </Card>
      ))}
    </div>
  );
};

export const Expandable = ({
  images,
  num_of_images_in_template,
  num_of_variations,
  rich_text_description_en,
  rich_text_description_ar,
  attributes,
  cover_image_id,
  setImage,
}: {
  images?: ImageType[];
  num_of_images_in_template: number;
  num_of_variations: number;
  rich_text_description_en: string;
  rich_text_description_ar: string;
  attributes: {
    attr_name_en: string;
    attr_name_ar: string;
  }[];
  cover_image_id?: number;
  setImage: ({
    public_s3_url,
    alt,
  }: {
    public_s3_url: string;
    alt: string;
  }) => void;
}) => {
  return (
    <div
      className="p-3"
      style={{
        backgroundColor: 'white',
        border: `1px solid ${Colors.greenMain}`,
        borderRadius: '12px',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
        }}
      >
        <div className="flex flex-col w-1/2">
          <p className="sectionTitle">Images</p>
          <div className="flex">
            <div className="flex flex-wrap">
              {images ? (
                images.map((img, index) => (
                  <img
                    key={index}
                    src={img.public_s3_url}
                    alt={img.alt}
                    style={{
                      width: '75px',
                      height: '75px',
                      borderRadius: '5px',
                      border:
                        img.id === cover_image_id ? '1px dashed green' : '',
                      marginRight: '1rem',
                      marginBottom: '1rem',
                    }}
                    onClick={() =>
                      setImage({
                        public_s3_url: img.public_s3_url,
                        alt: img.alt,
                      })
                    }
                  />
                ))
              ) : (
                <>Yes, {num_of_images_in_template}</>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-col w-1/2">
          <p className="sectionTitle">
            Attributes ({num_of_variations} variations)
          </p>
          <div className="flex">
            {attributes?.map((attr, index) => (
              <Chip
                key={index}
                color="primary"
                className="mr-2 font-normal"
                variant="flat"
              >
                <p>
                  {attr.attr_name_en}{' '}
                  {attr.attr_name_ar !== '' && ` - ${attr.attr_name_ar}`}
                </p>
              </Chip>
            ))}
          </div>
        </div>
      </div>
      <Spacer y={4} />
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          gap: '2rem',
        }}
      >
        <div className="flex flex-col w-1/2 max-h-64 overflow-auto">
          <p className="sectionTitle">Description (En)</p>
          <div className="flex">
            <SanitizedHTMLStyle
              style={{
                fontWeight: 'normal !important',
              }}
              dangerouslySetInnerHTML={{
                __html: rich_text_description_en,
              }}
            />
          </div>
        </div>{' '}
        <div className="flex flex-col w-1/2 max-h-64 overflow-auto">
          <p className="sectionTitle">Description (Ar)</p>
          <div className="flex">
            <SanitizedHTMLStyle
              style={{
                textAlign: 'right',
                fontWeight: 'normal !important',
              }}
              dangerouslySetInnerHTML={{
                __html: rich_text_description_ar,
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export const SilalExpandable = ({
  item,
  setImage,
}: {
  item: SolrTemplateResponse | SolrBuyerItemsResponse;
  setImage: ({
    public_s3_url,
    alt,
  }: {
    public_s3_url: string;
    alt: string;
  }) => void;
}) => {
  return Expandable({
    num_of_images_in_template:
      'num_of_images_in_template' in item
        ? item.num_of_images_in_template
        : item.num_of_images_in_listing,
    num_of_variations: item.num_of_variations,
    rich_text_description_en:
      'template_description_en' in item
        ? item.template_description_en
        : item.listing_description_en,
    rich_text_description_ar:
      'template_description_ar' in item
        ? item.template_description_ar
        : item.listing_description_ar,
    attributes: item.attribute_text_en
      .split(',')
      .filter((attr) => attr !== '' && attr !== ' ')
      .map((attr) => {
        return {
          attr_name_en: attr,
          attr_name_ar: '',
        };
      }),
    setImage,
  });
};
